import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, animate } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import api from "@/lib/api";
import { eur, num } from "@/lib/format";
import { Calculator, TrendingDown, TrendingUp, Receipt, Wallet, ExternalLink } from "lucide-react";

function AnimatedEuro({ value, className = "" }) {
  const [shown, setShown] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const controls = animate(prev.current, value || 0, {
      duration: 0.9, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setShown(v),
    });
    prev.current = value || 0;
    return () => controls.stop();
  }, [value]);
  return <span className={className}>{eur(shown)}</span>;
}

function Kpi({ testid, label, value, hint, tone, icon: Icon, delay }) {
  const tones = { valore: "text-[#1A2B4A]", incassato: "text-[#8a6d1f]", costi: "text-[#9b2c2c]", utile: "text-[#276749]", perdita: "text-[#9b2c2c]" };
  return (
    <motion.div data-testid={testid} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay, ease: "easeOut" }}
      className="rounded-md border border-[#1A2B4A]/10 bg-white p-4 flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-[0.12em] text-[#4a5568] flex items-center gap-1.5">
        <Icon className={`w-3.5 h-3.5 ${tones[tone]}`} /> {label}
      </span>
      <AnimatedEuro value={value} className={`text-2xl font-semibold tracking-tight leading-none ${tones[tone]}`} />
      {hint && <span className="text-[11px] text-[#4a5568]">{hint}</span>}
    </motion.div>
  );
}

// Costi delle pratiche e utile dello studio, complessivo e per tipo pratica.
export default function ContabilitaUtile({ filters = {} }) {
  const params = Object.fromEntries(
    Object.entries({ tipo_pratica: filters.tipo_pratica, da: filters.da, a: filters.a }).filter(([, v]) => v)
  );
  const { data, isLoading } = useQuery({
    queryKey: ["contabilita-utile", params],
    queryFn: async () => (await api.get("/contabilita/utile", { params })).data,
  });

  const t = data?.totali || {};
  const perTipo = data?.per_tipo || [];
  const costi = data?.costi || [];
  const pctCosti = t.valore ? Math.min(100, (t.costi / t.valore) * 100) : 0;

  return (
    <div data-testid="contabilita-utile" className="bg-white border border-[#1A2B4A]/12 rounded-lg p-4 mb-6 shadow-sm">
      <div className="flex items-center gap-2 mb-5">
        <Calculator className="w-4 h-4 text-[#C9A84C]" />
        <h3 className="text-lg font-semibold tracking-tight text-[#1A2B4A]">Costi e utile dello studio</h3>
      </div>

      {isLoading ? (
        <p className="text-sm text-[#4a5568]">Caricamento…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi testid="utile-valore" label="Valore pratiche" value={t.valore} tone="valore" icon={Receipt}
              hint="somma dei contratti" delay={0} />
            <Kpi testid="utile-incassato" label="Incassato" value={t.incassato} tone="incassato" icon={Wallet}
              hint={`da incassare ${eur((t.valore || 0) - (t.incassato || 0))}`} delay={0.08} />
            <Kpi testid="utile-costi" label="Costi pratiche" value={t.costi} tone="costi" icon={TrendingDown}
              hint={`spese vive ${eur(t.spese_vive)} · terzi ${eur(t.onorari_terzi)}`} delay={0.16} />
            <Kpi testid="utile-netto" label="Utile netto" value={t.utile} tone={(t.utile || 0) >= 0 ? "utile" : "perdita"}
              icon={TrendingUp} hint={`margine ${t.margine || 0}%`} delay={0.24} />
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between text-[10px] font-medium uppercase tracking-[0.08em] text-[#8a93a6] mb-1.5">
              <span>Composizione del valore complessivo</span>
              <span data-testid="utile-margine">{t.margine || 0}% di margine</span>
            </div>
            <div className="h-6 w-full rounded-full bg-[#f2f0ea] overflow-hidden flex" data-testid="utile-bar">
              <motion.div initial={{ width: 0 }} animate={{ width: `${pctCosti}%` }}
                transition={{ type: "spring", stiffness: 60, damping: 18 }}
                className="h-full bg-[#9b2c2c]/85 flex items-center justify-end pr-2">
                {pctCosti > 16 && <span className="text-[10px] text-white font-medium">costi {eur(t.costi)}</span>}
              </motion.div>
              <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(0, 100 - pctCosti)}%` }}
                transition={{ type: "spring", stiffness: 60, damping: 18, delay: 0.1 }}
                className="h-full bg-[#276749]/85 flex items-center pl-2">
                {100 - pctCosti > 20 && <span className="text-[10px] text-white font-medium">utile {eur(t.utile)}</span>}
              </motion.div>
            </div>
            {t.da_pagare > 0 && (
              <p className="text-[11px] text-[#9b2c2c] mt-2" data-testid="utile-da-pagare">
                Costi ancora da pagare: {eur(t.da_pagare)}
              </p>
            )}
          </div>

          {/* Utile per tipo pratica */}
          <div className="mt-6 grid lg:grid-cols-2 gap-5">
            <div data-testid="utile-chart">
              <h4 className="text-sm font-semibold text-[#1A2B4A] mb-3">Utile per tipo di pratica</h4>
              {perTipo.length === 0 ? (
                <p className="text-sm text-[#4a5568]">Nessuna pratica nel periodo selezionato.</p>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(180, perTipo.length * 62)}>
                  <BarChart data={perTipo} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#4a5568" }} tickFormatter={(v) => num(v, 0)} />
                    <YAxis type="category" dataKey="tipo" width={110} tick={{ fontSize: 11, fill: "#4a5568" }} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="costi" name="Costi" stackId="a" fill="#9b2c2c" radius={[0, 0, 0, 0]} animationDuration={900} />
                    <Bar dataKey="utile" name="Utile" stackId="a" fill="#276749" radius={[0, 4, 4, 0]} animationDuration={900} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold text-[#1A2B4A] mb-3">Dettaglio per tipo</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="utile-tabella">
                  <thead>
                    <tr className="text-left border-b border-[#1A2B4A]/10">
                      {["Tipo pratica", "N.", "Valore", "Costi", "Utile", "Margine"].map((h) => (
                        <th key={h} className="py-2 pr-3 text-[10px] uppercase tracking-wider text-[#4a5568]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {perTipo.map((r) => (
                      <tr key={r.tipo} data-testid={`utile-riga-${r.tipo}`} className="border-b border-[#1A2B4A]/5">
                        <td className="py-2 pr-3 text-[#1A2B4A]">{r.tipo}</td>
                        <td className="py-2 pr-3 text-[#4a5568]">{r.pratiche}</td>
                        <td className="py-2 pr-3">{eur(r.valore)}</td>
                        <td className="py-2 pr-3 text-[#9b2c2c]">{r.costi ? `− ${eur(r.costi)}` : eur(0)}</td>
                        <td className={`py-2 pr-3 font-semibold ${r.utile >= 0 ? "text-[#276749]" : "text-[#9b2c2c]"}`}>{eur(r.utile)}</td>
                        <td className="py-2 pr-3 text-[#4a5568]">{r.margine}%</td>
                      </tr>
                    ))}
                    {perTipo.length === 0 && (
                      <tr><td colSpan={6} className="py-3 text-center text-[#4a5568]">Nessun dato.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Elenco costi registrati sulle pratiche */}
          <div className="mt-6 pt-5 border-t border-[#1A2B4A]/8">
            <h4 className="text-sm font-semibold text-[#1A2B4A] mb-3">Costi registrati sulle pratiche</h4>
            {costi.length === 0 ? (
              <p className="text-sm text-[#4a5568]" data-testid="utile-costi-empty">
                Nessun costo registrato. I costi si inseriscono nella sezione "Contabilità della pratica" dentro ogni pratica.
              </p>
            ) : (
              <ul className="divide-y divide-[#1A2B4A]/5 max-h-72 overflow-y-auto" data-testid="utile-costi-list">
                {costi.map((c) => (
                  <li key={c.costo_id} data-testid={`utile-costo-${c.costo_id}`} className="flex items-center gap-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#1A2B4A] truncate">{c.descrizione}</div>
                      <div className="text-[11px] text-[#4a5568] truncate">
                        {c.cliente} · {c.tipo_pratica} · {c.tipo === "onorario_terzi" ? "onorario/fattura a terzi" : "spesa viva"}
                        {c.fornitore ? ` · ${c.fornitore}` : ""}
                        {c.data ? ` · ${new Date(c.data).toLocaleDateString("it-IT")}` : ""}
                        {c.pagato ? " · pagato" : " · da pagare"}
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-[#9b2c2c] shrink-0">− {eur(c.importo)}</span>
                    <Link to={`/pratiche/${c.pratica_id}`} data-testid={`utile-costo-link-${c.costo_id}`}
                      className="shrink-0 text-[#1A2B4A]/60 hover:text-[#C9A84C] transition-colors" title="Apri la pratica">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
